import React, { useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  RowSelectionState,
  OnChangeFn,
  useReactTable,
} from '@tanstack/react-table';
import { EntityCategories } from '../../../components/Categories';
import { IDictionaryBasicEventFieldsFragment } from '../../../utils/apollo/graphqlSchema';
import * as FormUI from '../../pageForm/FormUI';
import Table from '../../../components/Table';
import { eventsColumnsConfig } from './tableStylesConfig';
import { FavoriteBasicEvent } from '../../../components/Favorite';
import { TableList } from '../Fields/FieldsTable';
import EditableTextArea from '../../../components/EditableTextArea';
import {
  entityDescriptionSchema,
  entityNameSchema,
} from '../../../components/EditableTextArea/validation';
import GeneralFormItem from '../../../components/GeneralFormItem';
import ActionsMenu from '../../../components/ActionsMenu';
import { SingleSelectOption } from '../../../components/Dropdown/SingleSelect';
import { defaultPermissionTooltipOption } from '../../../components/KubitTooltip/globalTooltips';
import { IApplyBasicEventTag } from './types';
import {
  HeaderCheckbox,
  RowCheckbox,
} from '../../../components/Table/TableCheckbox';

interface IProps {
  events: IDictionaryBasicEventFieldsFragment[];
  schemaId: string;
  onApplyTags: (args: IApplyBasicEventTag) => Promise<void>;
  onRenameEvent: (
    event: IDictionaryBasicEventFieldsFragment,
    newName: string,
  ) => Promise<void>;
  onDescriptionChange: (
    event: IDictionaryBasicEventFieldsFragment,
    newDescription: string,
  ) => Promise<void>;
  onToggleEvent: (
    event: IDictionaryBasicEventFieldsFragment,
    isDisabled: boolean,
  ) => void;
  rowSelection: RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
  isPermittedToEditEvent: boolean;
  isPermittedToAssignCategory: boolean;
  preventBasicEventMetadataUpdates: boolean;
}

const initialState = {
  sorting: [
    {
      id: 'name',
      desc: false,
    },
  ],
};

const columnHelper = createColumnHelper<IDictionaryBasicEventFieldsFragment>();

const EventsTable = ({
  events,
  schemaId,
  onApplyTags,
  onRenameEvent,
  onDescriptionChange,
  onToggleEvent,
  rowSelection,
  setRowSelection,
  isPermittedToEditEvent,
  isPermittedToAssignCategory,
  preventBasicEventMetadataUpdates,
}: IProps) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor('key', {
        id: 'select',
        header: ({ table }) => <HeaderCheckbox table={table} />,
        cell: ({ row }) => <RowCheckbox row={row} />,
        meta: eventsColumnsConfig.select,
        enableSorting: false,
      }),
      columnHelper.accessor('key', {
        id: 'key',
        header: 'Key',
        cell: (info) => (
          <GeneralFormItem
            label={info.getValue()}
            tooltipContent={info.getValue()}
            isBold={true}
          />
        ),
        meta: eventsColumnsConfig.key,
        enableSorting: true,
      }),
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Name',
        cell: (info) => {
          const event = info.row.original;
          return (
            <EditableTextArea
              disabled={event.isDisabled}
              placeholder="Add Name..."
              value={event.name ?? ''}
              onChange={(newValue) => onRenameEvent(event, newValue)}
              defaultRows={2}
              isRenamed={event.isRenamed}
              validation={entityNameSchema}
              isReadOnly={
                !(isPermittedToEditEvent && !preventBasicEventMetadataUpdates)
              }
            />
          );
        },
        meta: eventsColumnsConfig.name,
        enableSorting: true,
      }),
      columnHelper.accessor('description', {
        id: 'description',
        header: 'Description',
        cell: (info) => {
          const event = info.row.original;
          return (
            <EditableTextArea
              disabled={event.isDisabled}
              placeholder={'Add Description...'}
              value={event.description ?? ''}
              onChange={(newValue) => onDescriptionChange(event, newValue)}
              validation={entityDescriptionSchema}
              isReadOnly={
                !(isPermittedToEditEvent && !preventBasicEventMetadataUpdates)
              }
            />
          );
        },
        meta: eventsColumnsConfig.description,
        enableSorting: true,
      }),
      columnHelper.accessor('tableNames', {
        id: 'tables',
        header: 'Tables',
        cell: (info) => (
          <FormUI.OverflowingTextWrapper>
            <TableList>
              {info.getValue()?.map((table) => <li key={table}>{table}</li>)}
            </TableList>
          </FormUI.OverflowingTextWrapper>
        ),
        meta: eventsColumnsConfig.tables,
        enableSorting: false,
      }),
      columnHelper.accessor('entityCategories', {
        id: 'tags',
        header: 'Tags',
        cell: (info) => {
          const event = info.row.original;

          return (
            <EntityCategories
              schemaId={schemaId}
              isDisabled={!isPermittedToAssignCategory || event.isDisabled}
              entityCategories={event.entityCategories}
              onApply={(newTags, isManaged) =>
                onApplyTags({
                  entities: [event],
                  newTags,
                  isManaged,
                  schemaId,
                })
              }
              isManaged={event.isManaged}
            />
          );
        },
        meta: eventsColumnsConfig.tags,
        enableSorting: true,
      }),
      columnHelper.accessor('key', {
        id: 'favorite',
        header: 'Favorite',
        cell: (info) => (
          <FavoriteBasicEvent schema={schemaId} event={info.row.original} />
        ),
        meta: eventsColumnsConfig.favorite,
        enableSorting: true,
      }),
      columnHelper.accessor('key', {
        id: 'actions',
        header: '',
        cell: (info) => {
          const event = info.row.original;
          return (
            <FormUI.ActionsContainer>
              <ActionsMenu>
                <SingleSelectOption
                  onClick={() => onToggleEvent(event, !event.isDisabled)}
                  iconName={event.isDisabled ? 'enable' : 'disable'}
                  label={event.isDisabled ? 'Enable' : 'Disable'}
                  disabled={!isPermittedToEditEvent}
                  tooltipOption={
                    !isPermittedToEditEvent
                      ? defaultPermissionTooltipOption
                      : undefined
                  }
                />
              </ActionsMenu>
            </FormUI.ActionsContainer>
          );
        },
        meta: eventsColumnsConfig.actions,
        enableSorting: false,
      }),
    ],
    [
      isPermittedToAssignCategory,
      isPermittedToEditEvent,
      onApplyTags,
      onDescriptionChange,
      onRenameEvent,
      onToggleEvent,
      preventBasicEventMetadataUpdates,
      schemaId,
    ],
  );

  const table = useReactTable({
    data: events,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState,
    getRowId: (originalRow) => originalRow.key,
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
  });

  return (
    <Table
      headerGroups={table.getHeaderGroups()}
      rows={table.getRowModel().rows}
      getIsDisabled={(row) => row.isDisabled}
    />
  );
};

export default EventsTable;
